import { OdProtosCasaData_Result } from '__generated__/athena';

import { listUnits, standardizeAddress } from '../components/api';

const UNIVERSAL_NIL_ADDRESS = '6aae647b-d603-5533-8464-c42c658b101a';

export { UNIVERSAL_NIL_ADDRESS };

export const ADDRESS_INVALID_RESULT: OdProtosCasaData_Result[] = [
  'ADDRESS_NIL',
  'ADDRESS_UNKNOWN',
  'ADDRESS_MISSING_PREMISE',
  'ADDRESS_INVALID_PREMISE',
  'ADDRESS_NOT_SUPPORTED',
  'ADDRESS_NOT_REAL',
  'ADDRESS_INVALID',
  'ADDRESS_INVALID_STREET',
  'ADDRESS_INVALID_SUB_PREMISE',
  'ADDRESS_MISSING_BOX_NUMBER',
  'ADDRESS_INVALID_BOX_NUMBER',
  'ADDRESS_EXTRANEOUS_INPUT',
];

export interface AddressForValidation {
  id?: string;
  street1: string;
  city: string;
  state: string;
  postal_code: string;
  unit?: string;
  latitude?: number;
  longitude?: number;
  result?: OdProtosCasaData_Result[];
  source?: 'google' | 'casa';
}

export interface AddressValidationResult {
  address: AddressForValidation;
  results: OdProtosCasaData_Result[];
  needsManualEntry: boolean;
  needsUnitSelection: boolean;
}

const doesAddressHaveMultipleUnits = async (addressUuid: string): Promise<boolean> => {
  const response = await listUnits(addressUuid);
  const addresses = response?.data?.casa?.listUnits?.addresses || [];

  return addresses.some(
    (address: any) =>
      address.uuid !== addressUuid &&
      !address.result?.some((casaResult: OdProtosCasaData_Result) =>
        ADDRESS_INVALID_RESULT.includes(casaResult),
      ),
  );
};

export const validateAddress = async (
  address: AddressForValidation,
): Promise<AddressValidationResult> => {
  let moreResult: OdProtosCasaData_Result[] = [];
  let casaAddress = address;

  // Standardize Google addresses to Casa format
  if (address.source === 'google') {
    const standardizeAddressResponse = await standardizeAddress(address);
    if (
      !standardizeAddressResponse ||
      !standardizeAddressResponse.data ||
      standardizeAddressResponse.errors ||
      standardizeAddressResponse.data?.casa.findByFields?.address?.uuid === UNIVERSAL_NIL_ADDRESS
    ) {
      moreResult = ['ADDRESS_UNKNOWN'];
      return {
        address: address,
        results: moreResult,
        needsManualEntry: true,
        needsUnitSelection: false,
      };
    }
    const addressResult = standardizeAddressResponse.data?.casa.findByFields?.address;
    casaAddress = {
      id: addressResult.uuid,
      street1: addressResult.street,
      city: addressResult.city,
      state: addressResult.state,
      postal_code: addressResult.postalCode,
      unit: addressResult.unitNumber,
      result: addressResult.result,
      latitude: address.latitude,
      longitude: address.longitude,
      source: 'casa',
    };
  }

  // Check if address needs a unit number
  if (!casaAddress?.result?.includes('ADDRESS_MISSING_SUB_PREMISE') && casaAddress?.unit === '') {
    if (casaAddress.id && (await doesAddressHaveMultipleUnits(casaAddress.id))) {
      moreResult = ['ADDRESS_MISSING_SUB_PREMISE'];
    }
  }

  const combinedResults = [...(casaAddress?.result || []), ...moreResult];

  // Check if address has invalid results
  const needsManualEntry = ADDRESS_INVALID_RESULT.some((error) => combinedResults.includes(error));
  const needsUnitSelection = combinedResults.includes('ADDRESS_MISSING_SUB_PREMISE');

  return {
    address: casaAddress,
    results: combinedResults,
    needsManualEntry,
    needsUnitSelection,
  };
};
